import { FC, useEffect, useState, useRef } from 'react';
import Glide from '@glidejs/glide';
import clsx from 'clsx';

import {
  calculateNewIndex,
  calculatePerView,
  getActiveDotIndex,
  glideOptions,
  handleFocusOnCard,
  handleKeyboardNavigation,
  handleResize,
  transformCards,
  updateTransform,
} from './cardCarouselUtils';
import { CardCarouselProps, GlideInstance } from './CardCarouselContainer.types';
import { StyledCardCarousel } from './CardCarouselContainer.styles';

export const CardCarouselContainer: FC<CardCarouselProps> = (props: CardCarouselProps) => {
  const {
    trackingProperties,
    componentId,
    noOfCards = 4,
    cardCount,
    cardWidth,
    cardHeight = '',
    showMiniArrows,
    children,
    selectedIndex,
    setSelectedIndex,
    isImageCarousel,
  } = props;

  const glideRef = useRef<HTMLDivElement | null>(null);
  const isKeyboardMove = useRef<boolean>(false);
  const isArrowClick = useRef<boolean>(false);
  const glideTrackRef = useRef<HTMLUListElement | null>(null);
  const [glideInstance, setGlideInstance] = useState<GlideInstance | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [activeDotIndex, setActiveDotIndex] = useState<number>(0);
  const currentTransformValue = glideTrackRef.current ? glideTrackRef?.current?.style.transform : '';

  // Set cardsPerView before Glide initialization
  const { perView: initialCardsPerView, cardWidthInPx } = calculatePerView(cardWidth, noOfCards, glideRef);

  useEffect(() => {
    const glide = new Glide(`#${componentId}`, {
      perView: initialCardsPerView, // Initialize with calculated perView
      startAt: currentIndex,
      rewind: isImageCarousel,
      ...glideOptions,
    });

    const mountedGlide = glide.mount();
    setGlideInstance(mountedGlide as GlideInstance);

    return () => {
      mountedGlide.destroy();
      setGlideInstance(null);
      if (isImageCarousel) {
        setCurrentIndex(0);
        setActiveDotIndex(0);
      }
    };
  }, [componentId, initialCardsPerView]);

  // Handle left/right arrow clicks
  const handleArrowClick = (e: React.SyntheticEvent | KeyboardEvent, direction: string) => {
    e.stopPropagation();
    isArrowClick.current = true;
    if (!glideInstance) return;
    const perView = isKeyboardMove.current ? 1 : initialCardsPerView;
    setCurrentIndex(prevState => {
      const newIndex = calculateNewIndex(direction, prevState, cardCount, perView, isImageCarousel);
      try {
        glideInstance.index = newIndex;
        glideInstance.update({});
      } catch (error) {
        console.log('Failed to update Glide instance:', error);
      }
      if (showMiniArrows)
        updateTransform(glideTrackRef, newIndex, newIndex, cardWidthInPx, initialCardsPerView, cardCount);
      return newIndex;
    });
  };

  useEffect(() => {
    if (!glideInstance || selectedIndex == null) return;

    const isInView = selectedIndex >= currentIndex && selectedIndex < currentIndex + initialCardsPerView;

    if (!isInView) {
      glideInstance.go(`=${selectedIndex}`);
      setCurrentIndex(selectedIndex);
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      handleKeyboardNavigation(e, glideInstance, cardCount, isKeyboardMove, handleArrowClick, isImageCarousel);
    };
    glideRef?.current?.addEventListener('keydown', handleKeyboard);

    return () => {
      glideRef?.current?.removeEventListener('keydown', handleKeyboard);
    };
  }, [glideInstance]);

  // Update cardsPerView on resize
  useEffect(() => {
    if (glideInstance) {
      glideInstance.on('resize', () => {
        handleResize(props, initialCardsPerView, glideInstance, glideRef, glideTrackRef, setCurrentIndex);
      });
    }
  }, [initialCardsPerView, glideInstance]);

  useEffect(() => {
    const handleFocus = () => {
      if (glideInstance && (isKeyboardMove.current || isArrowClick.current)) {
        handleFocusOnCard(
          cardWidthInPx,
          cardCount,
          currentIndex,
          initialCardsPerView,
          glideInstance,
          setCurrentIndex,
          glideTrackRef
        );
        isArrowClick.current = false;
        isKeyboardMove.current = false;
      }
    };

    // Listen for focus events within the carousel to detect when the focus moves to a sibling card
    if (glideTrackRef.current) {
      glideTrackRef.current.addEventListener('focusin', handleFocus, true);
    }

    return () => {
      if (glideTrackRef.current) {
        glideTrackRef.current.removeEventListener('focusin', handleFocus, true);
      }
    };
  }, [currentIndex, initialCardsPerView, cardCount, glideInstance, isKeyboardMove.current, isArrowClick.current]);

  useEffect(() => {
    transformCards(currentTransformValue, currentIndex, initialCardsPerView, cardCount, cardWidthInPx, glideTrackRef);
  }, [currentTransformValue]);

  useEffect(() => {
    setSelectedIndex?.(currentIndex);
    setActiveDotIndex(getActiveDotIndex(currentIndex, cardCount, initialCardsPerView));
  }, [currentIndex]);

  const leftArrowClassName = clsx(
    'left-arrow',
    'glide__arrow--left',
    !isImageCarousel && currentIndex === 0 ? (showMiniArrows ? 'hide-arrow' : 'd-none') : '',
    showMiniArrows ? 'mini-arrow' : '',
    trackingProperties?.clickTrack ? 'custom_click_track' : ''
  );

  const rightArrowClassName = clsx(
    'right-arrow',
    'glide__arrow--right',
    !isImageCarousel && currentIndex + initialCardsPerView >= cardCount
      ? showMiniArrows
        ? 'hide-arrow'
        : 'd-none'
      : '',
    showMiniArrows ? 'mini-arrow' : '',
    trackingProperties?.clickTrack ? 'custom_click_track' : ''
  );

  return (
    <StyledCardCarousel
      cardHeight={cardHeight}
      ref={glideRef}
      data-component-name="0-book-CardCarouselContainer"
      data-testid="CardCarouselContainer"
      className={clsx('col-12', isImageCarousel ? 'p-0' : 'pb-3 px-0')}
    >
      <div id={componentId} className="glide">
        <div className="glide__track" data-glide-el="track">
          <ul
            ref={glideTrackRef}
            className={clsx('glide__slides', isImageCarousel ? 'pb-0 mb-0' : 'pb-3')}
            id={`${componentId}__slides`}
          >
            {children(currentIndex)}
          </ul>
        </div>
        {cardCount > initialCardsPerView && (
          <div className="center-align">
            <div className={clsx('carouselControlType1', isImageCarousel ? 'carouselControlImageType' : '')}>
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={leftArrowClassName}
                  id={`${componentId}_left_arrow`}
                  data-glide-dir="<"
                  aria-label="left-arrow"
                  data-content={'Previous'}
                  onClick={e => handleArrowClick(e, 'left')}
                  disabled={!isImageCarousel && currentIndex === 0}
                  tabIndex={!isImageCarousel && currentIndex === 0 ? -1 : 0}
                  data-custom_click_track_value={trackingProperties?.leftArrow}
                >
                  <span className="icon-arrow-left" id={`${componentId}_left_arrow`} data-id="left"></span>
                </button>
              </div>
              <div id="control-nav">
                {Array.from({ length: Math.ceil(cardCount / initialCardsPerView) }, (_, i) => (
                  <button
                    className={activeDotIndex === i ? 'active' : ''}
                    data-glide-dir={i}
                    aria-label={`page-${i + 1}`}
                    key={initialCardsPerView + i}
                    tabIndex={-1}
                  />
                ))}
              </div>
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={rightArrowClassName}
                  id={`${componentId}_right_arrow`}
                  data-glide-dir=">"
                  aria-label="click to show more"
                  data-content={'Next'}
                  onClick={e => handleArrowClick(e, 'right')}
                  disabled={!isImageCarousel && currentIndex + initialCardsPerView >= cardCount}
                  tabIndex={!isImageCarousel && currentIndex + initialCardsPerView >= cardCount ? -1 : 0}
                  data-custom_click_track_value={trackingProperties?.rightArrow}
                >
                  <span className="icon-arrow-right" id={`${componentId}_right_arrow`} data-id="right"></span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StyledCardCarousel>
  );
};
